import { useMemo, useState } from "react";
import clsx from "clsx";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import type { AngebotNachfassGroup } from "@/lib/dokumente";
import { TimesheetBadge } from "@/pages/stundennachweise/TimesheetBadge";
import {
  daysUntilGroupReturns,
  formatAngebotResendLabel,
  formatDaysUntilNachfassDue,
  formatFollowUpCountLabel,
  getAngebotAgeThresholds,
  isAngebotNachfassOverdue,
  latestNachgefasstOffer,
  resolveAngebotAgeThreshold,
} from "@/pages/dokumente/angebotNachfassLogic";

const RECENT_AGE_LABEL = "≤ 1 Woche";

export function AngebotAgingPanels({
  groups,
  followedUpGroups = [],
  pendingGroups = [],
  loading,
  onOpenGroup,
}: {
  groups: AngebotNachfassGroup[];
  /** Ansprechpartner, deren Angebote alle nachgefasst sind und noch keine zwei Wochen warten. */
  followedUpGroups?: AngebotNachfassGroup[];
  /** Ansprechpartner mit offenem Angebot, die oben noch keine Kachel haben. */
  pendingGroups?: AngebotNachfassGroup[];
  loading?: boolean;
  onOpenGroup: (group: AngebotNachfassGroup) => void;
}) {
  const [followedOpen, setFollowedOpen] = useState(false);
  const [pendingOpen, setPendingOpen] = useState(false);
  const hasOverdue = groups.length > 0;
  const hasFollowed = followedUpGroups.length > 0;
  const hasPending = pendingGroups.length > 0;

  if (loading && !hasOverdue && !hasFollowed && !hasPending) {
    return (
      <div className="rounded-ios-lg bg-fill-quaternary/20 px-4 py-4 ring-1 ring-separator/30 dark:ring-separator/45">
        <h3 className="text-sm font-semibold text-label">Angebote &gt; 2 Wochen versendet</h3>
        <p className="mt-2 text-sm text-label-secondary">Lade Nachfass-Übersicht …</p>
      </div>
    );
  }

  if (!loading && !hasOverdue && !hasFollowed && !hasPending) return null;

  return (
    <div className="rounded-ios-lg bg-fill-quaternary/20 px-4 py-4 ring-1 ring-separator/30 dark:ring-separator/45">
      {hasOverdue ? (
        <>
          <h3 className="text-sm font-semibold text-label">Angebote &gt; 2 Wochen versendet</h3>
          <ul className="mt-3 grid grid-cols-[repeat(auto-fit,minmax(min(100%,14.5rem),1fr))] gap-2">
            {groups.map((group) => (
              <AngebotAgingTile key={group.key} group={group} variant="overdue" onOpen={onOpenGroup} />
            ))}
          </ul>
        </>
      ) : null}

      {hasFollowed ? (
        <div className={clsx(hasOverdue && "mt-3 border-t border-separator/40 pt-3")}>
          <button
            type="button"
            aria-expanded={followedOpen}
            aria-controls="angebot-followed-tiles"
            onClick={() => setFollowedOpen((open) => !open)}
            className="flex w-full items-center gap-2 rounded-ios px-1 py-1.5 text-left hover:bg-fill-tertiary"
          >
            {followedOpen ? (
              <ChevronDownIcon className="h-4 w-4 shrink-0 text-label-secondary" aria-hidden />
            ) : (
              <ChevronRightIcon className="h-4 w-4 shrink-0 text-label-secondary" aria-hidden />
            )}
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-label">Erneut versendet</span>
              <span className="block text-xs font-normal text-label-secondary">
                Alle fälligen Angebote wurden nachgefasst. Sie erscheinen wieder oben, sobald erneut
                zwei Wochen vergangen sind.
              </span>
            </span>
            <span className="shrink-0 text-xs font-medium text-label-secondary">
              {followedUpGroups.length}
            </span>
          </button>
          {followedOpen ? (
            <ul
              id="angebot-followed-tiles"
              className="mt-3 grid grid-cols-[repeat(auto-fit,minmax(min(100%,14.5rem),1fr))] gap-2"
            >
              {followedUpGroups.map((group) => (
                <AngebotAgingTile key={group.key} group={group} variant="followed" onOpen={onOpenGroup} />
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {hasPending ? (
        <div className={clsx((hasOverdue || hasFollowed) && "mt-3 border-t border-separator/40 pt-3")}>
          <button
            type="button"
            aria-expanded={pendingOpen}
            aria-controls="angebot-pending-tiles"
            onClick={() => setPendingOpen((open) => !open)}
            className="flex w-full items-center gap-2 rounded-ios px-1 py-1.5 text-left hover:bg-fill-tertiary"
          >
            {pendingOpen ? (
              <ChevronDownIcon className="h-4 w-4 shrink-0 text-label-secondary" aria-hidden />
            ) : (
              <ChevronRightIcon className="h-4 w-4 shrink-0 text-label-secondary" aria-hidden />
            )}
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-label">
                Weitere Ansprechpartner ohne Rückmeldung
              </span>
              <span className="block text-xs font-normal text-label-secondary">
                Versendete Angebote, die noch keine zwei Wochen offen sind.
              </span>
            </span>
            <span className="shrink-0 text-xs font-medium text-label-secondary">{pendingGroups.length}</span>
          </button>
          {pendingOpen ? (
            <ul
              id="angebot-pending-tiles"
              className="mt-3 grid grid-cols-[repeat(auto-fit,minmax(min(100%,14.5rem),1fr))] gap-2"
            >
              {pendingGroups.map((group) => (
                <AngebotAgingTile key={group.key} group={group} variant="pending" onOpen={onOpenGroup} />
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function AngebotAgingTile({
  group,
  variant,
  onOpen,
}: {
  group: AngebotNachfassGroup;
  variant: "overdue" | "pending" | "followed";
  onOpen: (group: AngebotNachfassGroup) => void;
}) {
  const { distribution, maxThreshold, count, newerCount, followedCount } = useMemo(
    () => summarizeAngebotGroup(group, variant),
    [group, variant],
  );
  const countLabel = count === 1 ? "Angebot" : "Angebote";
  const maxLabel = maxThreshold?.label || (variant === "pending" ? RECENT_AGE_LABEL : "> 2 Wochen");
  const badges = [
    ...getAngebotAgeThresholds().filter((t) => distribution.has(t.label)),
    ...(distribution.has(RECENT_AGE_LABEL) ? [{ label: RECENT_AGE_LABEL, className: "", subtle: true }] : []),
  ];
  const latestFollowUp = variant === "followed" ? latestNachgefasstOffer(group.angebote) : null;
  const followUpLabel = latestFollowUp
    ? formatAngebotResendLabel({ ...latestFollowUp, overdue: false })
    : null;
  const returnLabel =
    variant === "followed" ? formatDaysUntilNachfassDue(daysUntilGroupReturns(group)) : null;

  return (
    <li className="min-w-0">
      <button
        type="button"
        onClick={() => onOpen(group)}
        className={clsx(
          "flex w-full min-h-[5.25rem] min-w-0 flex-col gap-2 rounded-ios px-3 py-3 text-left text-sm transition ring-1",
          "bg-card ring-separator/40 hover:bg-fill-tertiary",
        )}
      >
        <span className="truncate font-semibold text-label">{group.name}</span>
        {variant === "followed" ? (
          <span className="text-xs text-label-secondary">
            {followedCount === count
              ? `${count} ${countLabel} nachgefasst`
              : `${count} ${countLabel} · ${followedCount} nachgefasst`}
            {followUpLabel ? ` · ${followUpLabel}` : ""}
          </span>
        ) : (
          <span className="text-xs text-label-secondary">
            {count} {countLabel}, max{" "}
            {maxThreshold ? (
              <TimesheetBadge compact className={maxThreshold.className}>
                {maxLabel}
              </TimesheetBadge>
            ) : variant === "pending" ? (
              <TimesheetBadge compact subtle>
                {maxLabel}
              </TimesheetBadge>
            ) : (
              maxLabel
            )}
            {newerCount > 0 ? ` · ${newerCount} weitere offen` : ""}
          </span>
        )}
        <span className="flex flex-wrap gap-1">
          {variant === "followed" && returnLabel ? (
            <TimesheetBadge compact className="bg-sky-600 text-white">
              {returnLabel}
            </TimesheetBadge>
          ) : null}
          {variant === "overdue" && followedCount > 0 ? (
            <TimesheetBadge compact className="bg-sky-600 text-white">
              {formatFollowUpCountLabel(group.angebote)}
            </TimesheetBadge>
          ) : null}
          {variant === "followed"
            ? null
            : badges.map((t) => (
                <TimesheetBadge
                  key={t.label}
                  compact
                  subtle={"subtle" in t && t.subtle}
                  className={t.className || undefined}
                >
                  {distribution.get(t.label)}× {t.label}
                </TimesheetBadge>
              ))}
        </span>
      </button>
    </li>
  );
}

function summarizeAngebotGroup(
  group: AngebotNachfassGroup,
  variant: "overdue" | "pending" | "followed",
) {
  const distribution = new Map<string, number>();
  const counted =
    variant === "overdue"
      ? group.angebote.filter((angebot) => isAngebotNachfassOverdue(angebot))
      : group.angebote;
  let maxThreshold = resolveAngebotAgeThreshold(
    counted.reduce((max, angebot) => Math.max(max, Number(angebot.age_days) || 0), 0),
  );
  if (variant !== "followed") {
    for (const angebot of counted) {
      const threshold = resolveAngebotAgeThreshold(angebot.age_days);
      if (!threshold) {
        if (variant === "pending") {
          distribution.set(RECENT_AGE_LABEL, (distribution.get(RECENT_AGE_LABEL) || 0) + 1);
        }
        continue;
      }
      distribution.set(threshold.label, (distribution.get(threshold.label) || 0) + 1);
      if (!maxThreshold || threshold.days > maxThreshold.days) maxThreshold = threshold;
    }
  }
  const overdueCount = group.angebote.filter((angebot) => isAngebotNachfassOverdue(angebot)).length;
  const followedCount = group.angebote.filter((angebot) => Boolean(angebot.nachgefasst_am)).length;
  const newerCount = group.angebote.filter(
    (angebot) => !isAngebotNachfassOverdue(angebot) && !angebot.nachgefasst_am,
  ).length;
  if (variant === "followed") {
    return {
      distribution,
      maxThreshold,
      count: group.angebote.length,
      newerCount: 0,
      followedCount,
    };
  }
  if (variant === "pending") {
    return {
      distribution,
      maxThreshold,
      count: group.angebote.length,
      newerCount: 0,
      followedCount,
    };
  }
  return {
    distribution,
    maxThreshold,
    count: overdueCount,
    newerCount,
    followedCount,
  };
}
